import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Building2,
  Phone,
  Mail,
  Bell,
  Image as ImageIcon,
  Shield,
  Zap,
  Smartphone,
  ClipboardCheck,
  DollarSign,
  Handshake,
  Download,
  Receipt,
  Share2,
  CheckCircle2,
  ArrowRight,
  Hammer,
  MapPin,
  Sparkles,
  Quote,
  Play,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { HERO_IMAGE, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import MascotChat from "@/components/mascot/MascotChat";
import { MascotWelcome, MascotStanding } from "@/components/mascot/MascotParts";

/* ------------------------------------------------------------------ */
/* Shared helpers                                                      */
/* ------------------------------------------------------------------ */
const WHATSAPP_NUMBER = "5511956649968";
const EMAIL = "amcescolas@gmail.com";
const SITE_DOMAIN = "amcescolas.com";

const whatsappLink = (text) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

const ACCESS_MESSAGE =
  "Olá AMC Escolas! Sou diretor(a) e gostaria de solicitar acesso ao Portal da Escola para acompanhar os orçamentos e serviços.";

/* ------------------------------------------------------------------ */
/* Animated counter                                                    */
/* ------------------------------------------------------------------ */
const Counter = ({ end, duration = 1800, suffix = "+", testid }) => {
  const [value, setValue] = useState(0);
  const ref = React.useRef(null);
  const started = React.useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const p = Math.min(1, (now - start) / duration);
              // easeOutCubic
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(Math.round(end * eased));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref} data-testid={testid}>
      {value.toLocaleString("pt-BR")}
      {suffix}
    </span>
  );
};

const STATS = [
  { end: 30,   suffix: "+", label: "anos de experiência",   icon: Shield,     grad: "from-amber-400 to-orange-500" },
  { end: 500,  suffix: "+", label: "escolas atendidas",     icon: Building2,  grad: "from-emerald-400 to-teal-500" },
  { end: 5999, suffix: "+", label: "orçamentos realizados", icon: FileText,   grad: "from-violet-400 to-indigo-500" },
];

const Stats = () => (
  <Section className="bg-white -mt-6">
    <div className="rounded-3xl bg-gradient-to-br from-[#1e3a8a] via-[#2a4ba0] to-[#4c1d95] text-white px-6 py-10 md:px-10 md:py-12 shadow-2xl shadow-indigo-900/20 relative overflow-hidden">
      <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/5" />
      <div className="absolute -left-20 bottom-0 w-64 h-64 rounded-full bg-white/5" />
      <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-10 text-center">
        {STATS.map((s, idx) => (
          <div key={s.label} className="flex flex-col items-center" data-testid={`stat-${idx}`}>
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${s.grad} flex items-center justify-center mb-3 shadow-lg`}>
              <s.icon className="w-6 h-6 text-white" strokeWidth={2.3} />
            </div>
            <p className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-none">
              <Counter end={s.end} suffix={s.suffix} testid={`stat-value-${idx}`} />
            </p>
            <p className="mt-2 text-xs md:text-sm font-semibold text-white/80 uppercase tracking-wider">
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* Section wrapper                                                     */
/* ------------------------------------------------------------------ */
const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-14 md:py-20 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 md:px-6">{children}</div>
  </section>
);

const Kicker = ({ children }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7c3aed]/10 text-[#7c3aed] text-[11px] font-bold uppercase tracking-wider">
    <Sparkles className="w-3 h-3" />
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */
const PrimaryCTA = ({ children, testid, large = false, className = "" }) => (
  <a
    href={whatsappLink(ACCESS_MESSAGE)}
    target="_blank"
    rel="noreferrer"
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#7c3aed] to-[#5b21b6] text-white font-bold shadow-lg shadow-violet-900/20 hover:shadow-xl hover:-translate-y-0.5 transition-all ${
      large ? "px-6 py-3.5 text-base" : "px-5 py-2.5 text-sm"
    } ${className}`}
  >
    <Phone className="w-4 h-4" />
    {children}
    <ArrowRight className="w-4 h-4" />
  </a>
);

const SecondaryCTA = ({ to, children, testid, className = "" }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-[#1e3a8a]/20 bg-white text-[#1e3a8a] text-sm font-bold hover:bg-[#1e3a8a]/5 transition-colors ${className}`}
  >
    {children}
  </Link>
);

/* ------------------------------------------------------------------ */
/* Header                                                              */
/* ------------------------------------------------------------------ */
const Header = () => {
  const [solid, setSolid] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // When viewing landing while logged in (via ?preview=1 or similar),
  // show a single contextual CTA that returns the user to their panel.
  const panelLink =
    user?.role === "admin"
      ? { to: "/", label: "Painel administrador", icon: Shield, testid: "header-go-admin-panel" }
      : user?.role === "school"
      ? { to: "/escola", label: "Portal da escola", icon: Building2, testid: "header-go-school-portal" }
      : null;
  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all ${
        solid
          ? "bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      }`}
      data-testid="landing-header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Logo compact withIcon />
        <nav className="hidden lg:flex items-center gap-7 text-sm font-semibold text-slate-600">
          <a href="#servicos" className="hover:text-[#1e3a8a]">Serviços</a>
          <a href="#plataforma" className="hover:text-[#1e3a8a]">Plataforma</a>
          <a href="#como-funciona" className="hover:text-[#1e3a8a]">Como funciona</a>
          <a href="#contato" className="hover:text-[#1e3a8a]">Contato</a>
        </nav>
        <div className="flex items-center gap-2">
          {panelLink ? (
            <Link
              to={panelLink.to}
              data-testid={panelLink.testid}
              className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-sm font-bold text-white bg-[#1e3a8a] hover:bg-[#1e40af] transition-colors shadow-sm"
            >
              <panelLink.icon className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">{panelLink.label}</span>
              <span className="sm:hidden">Voltar</span>
            </Link>
          ) : (
            <>
              <Link
                to="/escola/login"
                data-testid="header-login-diretor"
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-sm font-bold text-[#1e3a8a] bg-[#1e3a8a]/5 border border-[#1e3a8a]/15 hover:bg-[#1e3a8a]/10 transition-colors"
              >
                <Building2 className="w-4 h-4" />
                <span className="hidden xs:inline sm:inline">Diretor(a)</span>
              </Link>
              <Link
                to="/login"
                data-testid="header-login-admin"
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-sm font-bold text-white bg-[#1e3a8a] hover:bg-[#1e40af] transition-colors shadow-sm"
              >
                <Shield className="w-4 h-4" />
                <span className="hidden xs:inline sm:inline">Admin</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */
const Hero = () => (
  <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden" data-testid="landing-hero">
    <div
      className="absolute inset-0 -z-10"
      style={{
        background:
          "radial-gradient(1200px 500px at 15% 10%, rgba(124,58,237,0.18), transparent 55%), radial-gradient(900px 500px at 90% 20%, rgba(30,58,138,0.18), transparent 60%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
      }}
    />
    <div className="max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <MascotWelcome />
        <br />
        <Kicker>Sistema AMC Escolas</Kicker>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0f172a] leading-[1.05]">
          Cuidamos da sua escola,
          <span className="block bg-gradient-to-r from-[#7c3aed] to-[#1e3a8a] bg-clip-text text-transparent">
            você cuida do futuro.
          </span>
        </h1>
        <p className="mt-5 text-base md:text-lg text-slate-600 max-w-xl">
          Mais de <span className="font-bold text-[#1e3a8a]">30 anos</span> de manutenção e
          serviços profissionais para escolas municipais e estaduais — agora com um portal
          digital exclusivo para diretores(as) acompanharem orçamentos, aprovações e
          serviços em tempo real.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <PrimaryCTA testid="hero-cta-primary" large>
            Solicitar acesso Diretor(a)
          </PrimaryCTA>
          <SecondaryCTA to="/escola/login" testid="hero-cta-login" className="px-6 py-3.5 text-base">
            <Building2 className="w-4 h-4" /> Já tenho acesso
          </SecondaryCTA>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Sem instalação</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Funciona no celular</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Notificações em tempo real</span>
        </div>
      </div>

      <div className="relative">
        <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-indigo-900/20 border border-white">
          <img
            src={HERO_IMAGE}
            alt="Equipe AMC Escolas"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/70 via-transparent to-transparent" />
          <div className="absolute left-4 right-28 md:right-24 bottom-4 rounded-2xl bg-black/35 backdrop-blur px-4 py-3">
            <p className="text-[11px] uppercase tracking-widest text-white/80 font-bold">AMC Escolas</p>
            <p className="font-heading text-white font-extrabold text-base md:text-lg">
              Já são 3 décadas mantendo escolas em dia
            </p>
          </div>
        </div>

        {/* Floating stat chips */}
        <div className="hidden md:flex absolute -left-6 top-10 bg-white rounded-2xl px-4 py-3 shadow-xl border border-slate-100 items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <p className="font-heading font-black text-[#1e3a8a] text-lg leading-none">30+ anos</p>
            <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">de experiência</p>
          </div>
        </div>
        <div className="hidden md:flex absolute -left-6 bottom-24 bg-white rounded-2xl px-4 py-3 shadow-xl border border-slate-100 items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-100 text-[#7c3aed] flex items-center justify-center">
            <Bell className="w-5 h-5" />
          </div>
          <div>
            <p className="font-heading font-black text-[#1e3a8a] text-lg leading-none">Push</p>
            <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">em tempo real</p>
          </div>
        </div>
        <MascotStanding />
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/* Services                                                            */
/* ------------------------------------------------------------------ */
const SERVICES = [
  { icon: Hammer,  title: "Manutenção predial", desc: "Reparos civis, hidráulica, elétrica, pintura e marcenaria com mão de obra qualificada." },
  { icon: Zap,     title: "Instalações elétricas", desc: "Projetos, laudos, troca de disjuntores, iluminação e padrão de entrada." },
  { icon: Shield,  title: "Segurança e vedação", desc: "Grades, telas de proteção, limpeza de caixa d'água e vistorias." },
  { icon: MapPin,  title: "Atendimento regional", desc: "Equipe própria atendendo escolas municipais e estaduais em toda a região." },
];

const Services = () => (
  <Section id="servicos" className="bg-white">
    <div className="text-center max-w-2xl mx-auto">
      <Kicker>O que fazemos</Kicker>
      <h2 className="mt-3 font-heading text-3xl md:text-4xl font-black text-[#1e3a8a]">
        Manutenção profissional, do telhado ao disjuntor
      </h2>
      <p className="mt-3 text-slate-600">
        Resolvemos tudo que a escola precisa com orçamento transparente, nota fiscal
        e acompanhamento online pela direção.
      </p>
    </div>
    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {SERVICES.map((s) => (
        <div
          key={s.title}
          className="group p-5 rounded-2xl border border-slate-100 bg-white hover:border-[#7c3aed]/40 hover:-translate-y-1 transition-all shadow-sm hover:shadow-xl"
        >
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#7c3aed]/10 to-[#1e3a8a]/10 flex items-center justify-center text-[#7c3aed] group-hover:scale-110 transition-transform">
            <s.icon className="w-5 h-5" strokeWidth={2.3} />
          </div>
          <h3 className="mt-4 font-heading font-black text-[#1e3a8a]">{s.title}</h3>
          <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">{s.desc}</p>
        </div>
      ))}
    </div>
    <div className="mt-10 flex justify-center">
      <PrimaryCTA testid="services-cta" large>Solicitar acesso Diretor(a)</PrimaryCTA>
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* Platform showcase (features)                                        */
/* ------------------------------------------------------------------ */
const FEATURES = [
  { icon: FileText,       title: "Orçamentos digitais",      desc: "Receba, visualize e aprove orçamentos em poucos toques, direto do celular." },
  { icon: ClipboardCheck, title: "Aprovação em tempo real", desc: "Direção aprova com observações; AMC recebe push na hora e inicia o serviço." },
  { icon: Handshake,      title: "Faz Depois Paga (FDP)",   desc: "Fluxo exclusivo para escolas, com controle de crédito, limites e prazos." },
  { icon: Receipt,        title: "Nota fiscal integrada",   desc: "ISS e Simples calculados automaticamente, com NF vinculada ao orçamento." },
  { icon: Share2,         title: "PDF + WhatsApp",          desc: "Compartilhe orçamentos em PDF profissional com um toque, direto no WhatsApp." },
  { icon: Bell,           title: "Notificações push",       desc: "Avisos instantâneos de novos itens, aprovações, pagamentos e observações." },
  { icon: ImageIcon,      title: "Fotos dos serviços",      desc: "Galeria de antes e depois de cada atendimento, acessível para a direção." },
  { icon: Smartphone,     title: "App na tela inicial",     desc: "Instale o AMC Escolas no celular como aplicativo (PWA). Abre rápido, direto da tela inicial." },
];

const Platform = () => (
  <Section id="plataforma" className="bg-gradient-to-b from-slate-50 to-white">
    <div className="text-center max-w-2xl mx-auto">
      <Kicker>Portal digital</Kicker>
      <h2 className="mt-3 font-heading text-3xl md:text-4xl font-black text-[#1e3a8a]">
        Tudo que a direção precisa, em um só lugar
      </h2>
      <p className="mt-3 text-slate-600">
        Um portal exclusivo para diretores(as) acompanharem a manutenção da escola com
        total transparência — e um painel admin completo para a equipe AMC.
      </p>
    </div>
    <div className="mt-12 grid lg:grid-cols-3 gap-6">
      {/* Mockup card */}
      <div className="lg:col-span-1 relative">
        <PhoneMockup />
      </div>
      {/* Features grid */}
      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
        {FEATURES.map((f, i) => (
          <div
            key={f.title}
            className="p-4 rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-md transition-shadow flex gap-3 items-start"
            data-testid={`feature-${i}`}
          >
            <div className="w-10 h-10 rounded-xl bg-[#7c3aed]/10 text-[#7c3aed] flex items-center justify-center shrink-0">
              <f.icon className="w-5 h-5" strokeWidth={2.3} />
            </div>
            <div>
              <h4 className="font-heading font-black text-[#1e3a8a] text-sm">{f.title}</h4>
              <p className="text-[12.5px] text-slate-600 leading-snug mt-0.5">{f.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="mt-12 flex justify-center">
      <PrimaryCTA testid="platform-cta" large>Solicitar acesso Diretor(a)</PrimaryCTA>
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* Testimonials                                                        */
/* ------------------------------------------------------------------ */
const TESTIMONIALS = [
  {
    quote:
      "Antes eu precisava ligar várias vezes pra saber do orçamento. Agora recebo tudo no celular e aprovo com um toque. A escola nunca ficou tão organizada.",
    name: "Cibele",
    role: "Diretora",
    school: "EMEB Antônio Pereira Coutinho",
    city: "São Bernardo do Campo — SP",
  },
  {
    quote:
      "O portal da AMC transformou a relação com a manutenção. As notificações no celular avisam de cada etapa, e o PDF com nota fiscal já chega pronto pra enviar à prefeitura.",
    name: "Edna",
    role: "Diretora",
    school: "EMEB Professora Dolores de Toledo de Matteo",
    city: "São Bernardo do Campo — SP",
  },
  {
    quote:
      "A rapidez da equipe e a transparência dos orçamentos nos devolveu tempo pra focar no que importa: as crianças. Virou referência pra outras APMs da região.",
    name: "Anderson",
    role: "Diretor",
    school: "Escola Estadual Santa Olímpia",
    city: "São Bernardo do Campo — SP",
  },
];

const Testimonials = () => (
  <Section id="depoimentos" className="bg-gradient-to-b from-slate-50 to-white">
    <div className="text-center max-w-2xl mx-auto">
      <Kicker>Diretores aprovam</Kicker>
      <h2 className="mt-3 font-heading text-3xl md:text-4xl font-black text-[#1e3a8a]">
        O que a direção está dizendo
      </h2>
      <p className="mt-3 text-slate-600">
        Depoimentos de diretoras(es) das escolas atendidas pela AMC sobre o novo sistema.
      </p>
    </div>
    <div className="mt-10 grid md:grid-cols-3 gap-5">
      {TESTIMONIALS.map((t, i) => (
        <div
          key={t.name + i}
          className="relative p-6 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
          data-testid={`testimonial-${i}`}
        >
          <Quote className="w-8 h-8 text-[#7c3aed]/15 absolute top-4 right-4" />
          <p className="text-[14px] text-slate-700 leading-relaxed">"{t.quote}"</p>
          <div className="mt-5 flex items-center gap-3 pt-4 border-t border-slate-100">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1e3a8a] to-[#7c3aed] flex items-center justify-center text-white font-black">
              {t.name[0]}
            </div>
            <div>
              <p className="font-heading font-black text-[#1e3a8a] text-sm leading-tight">
                {t.name} <span className="text-slate-500 font-semibold">· {t.role}</span>
              </p>
              <p className="text-[11px] text-slate-600 leading-snug mt-0.5">{t.school}</p>
              <p className="text-[10px] text-slate-400">{t.city}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* Admin-managed media gallery — split in 3 sections                   */
/* ------------------------------------------------------------------ */
const SECTION_META = {
  portal_escola: {
    id: "portal-escola",
    kicker: "Portal da escola",
    title: "O que a direção enxerga no celular",
    desc:
      "Capturas reais do portal utilizadas pelos(as) diretores(as) das escolas atendidas pela AMC — sem edição.",
    bg: "bg-gradient-to-b from-slate-50 to-white",
    frameFrom: "from-[#1e3a8a]",
    frameTo: "to-[#4c1d95]",
  },
  painel_admin: {
    id: "painel-admin",
    kicker: "Painel administrativo",
    title: "A equipe AMC no comando",
    desc:
      "O painel que a equipe AMC usa para gerenciar escolas, orçamentos, NF, FDP e financeiro em tempo real.",
    bg: "bg-gradient-to-b from-indigo-50/40 to-white",
    frameFrom: "from-slate-900",
    frameTo: "to-[#1e3a8a]",
  },
  trabalhos: {
    id: "trabalhos",
    kicker: "Trabalhos realizados",
    title: "Fotos e vídeos dos serviços",
    desc: "Resultados reais do nosso dia a dia atendendo escolas municipais e estaduais.",
    bg: "bg-gradient-to-b from-slate-50 to-white",
    frameFrom: "from-emerald-600",
    frameTo: "to-emerald-800",
  },
};

const SECTION_ORDER = ["portal_escola", "painel_admin", "trabalhos"];

const MediaFrame = ({ m, meta }) => {
  const src = `/api/files/${m.storage_path}`;
  return (
    <figure
      className={`group rounded-2xl overflow-hidden bg-gradient-to-b ${meta.frameFrom} ${meta.frameTo} p-2 shadow-xl hover:-translate-y-1 transition-all relative`}
      data-testid={`media-${m.id}`}
    >
      <div className="rounded-xl overflow-hidden bg-white">
        {m.kind === "video" ? (
          <video src={src} controls playsInline preload="metadata" className="w-full h-auto block bg-black" />
        ) : (
          <img src={src} alt={m.caption || "Mídia"} loading="lazy" className="w-full h-auto block" />
        )}
      </div>
      {m.caption && (
        <figcaption className="text-[11px] text-white/90 leading-snug px-1 pt-2 pb-1">{m.caption}</figcaption>
      )}
      {m.kind === "video" && (
        <div className="pointer-events-none absolute top-4 right-4 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
          <Play className="w-3.5 h-3.5 text-white" fill="white" />
        </div>
      )}
    </figure>
  );
};

/* --- Lightbox used by the compact "trabalhos" gallery ----------------- */
const Lightbox = ({ items, index, onClose, onPrev, onNext }) => {
  const videoRef = React.useRef(null);
  const m = index != null ? items[index] : null;
  // Trim range for videos (admin can set start/end seconds). The ref applies
  // it on load and auto-pauses when the end is reached.
  const start = Math.max(0, Number(m?.trim_start_sec || 0));
  const end = m && m.trim_end_sec != null ? Number(m.trim_end_sec) : null;

  useEffect(() => {
    if (index == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [index, onClose, onPrev, onNext]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !m || m.kind !== "video") return;
    const seek = () => { try { v.currentTime = start; } catch {} };
    v.addEventListener("loadedmetadata", seek, { once: true });
    const onTime = () => {
      if (end != null && v.currentTime >= end) {
        v.pause();
        try { v.currentTime = end; } catch {}
      }
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [index, start, end, m]);

  if (index == null || !m) return null;
  const src = `/api/files/${m.storage_path}`;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4 md:p-8 animate-in fade-in duration-200"
      data-testid="landing-lightbox"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur transition"
        aria-label="Fechar"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur transition disabled:opacity-30"
        aria-label="Anterior"
        disabled={items.length < 2}
        data-testid="lightbox-prev"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur transition disabled:opacity-30"
        aria-label="Próximo"
        disabled={items.length < 2}
        data-testid="lightbox-next"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
      <div
        className="relative max-w-[92vw] max-h-[85vh] w-auto h-auto flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {m.kind === "video" ? (
          <video
            key={m.id}
            ref={videoRef}
            src={src}
            controls
            autoPlay
            playsInline
            className="max-h-[80vh] max-w-full rounded-xl bg-black shadow-2xl"
          />
        ) : (
          <img
            src={src}
            alt={m.caption || "Mídia"}
            className="max-h-[80vh] max-w-full rounded-xl shadow-2xl object-contain"
          />
        )}
        <div className="mt-3 text-center text-white/90 text-xs">
          <span className="font-semibold">{index + 1}</span>
          <span className="opacity-70"> / {items.length}</span>
          {m.caption && <span className="opacity-80"> — {m.caption}</span>}
        </div>
      </div>
    </div>
  );
};

/* --- Compact thumbnail used in the "trabalhos" grid ------------------- */
const WorkThumb = ({ m, onOpen }) => {
  const src = `/api/files/${m.storage_path}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative aspect-square rounded-lg overflow-hidden bg-slate-900 shadow-md hover:shadow-xl transition-all hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
      data-testid={`work-thumb-${m.id}`}
    >
      {m.kind === "video" ? (
        <>
          <video
            src={src}
            muted
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/35 group-hover:bg-black/20 transition" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition">
              <Play className="w-4 h-4 text-emerald-700 ml-0.5" fill="currentColor" />
            </div>
          </div>
        </>
      ) : (
        <>
          <img
            src={src}
            alt={m.caption || "Trabalho"}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
        </>
      )}
    </button>
  );
};

/* ------------------------------------------------------------------ */
/* "Fotos e vídeos dos serviços" — compact grid moved to the top of    */
/* the landing. Opens a Lightbox with arrow navigation on click.       */
/* ------------------------------------------------------------------ */
const WorkGallery = () => {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [lbIndex, setLbIndex] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/landing/media?featured=true&section=trabalhos");
        const list = (Array.isArray(r.data) ? r.data : []).filter(
          (m) => (m.section || "trabalhos") === "trabalhos",
        );
        setItems(list);
        try {
          const all = await api.get("/landing/media?limit=500&section=trabalhos", { silent: true });
          const total = Array.isArray(all.data)
            ? all.data.filter((m) => (m.section || "trabalhos") === "trabalhos").length
            : list.length;
          setTotalCount(total);
        } catch {
          setTotalCount(list.length);
        }
      } catch {
        setItems([]);
      }
    })();
  }, []);

  if (!items.length) return null;
  const meta = SECTION_META.trabalhos;
  const hasMore = totalCount > items.length;

  return (
    <Section id={meta.id} className={meta.bg}>
      <div className="text-center max-w-2xl mx-auto">
        <Kicker>{meta.kicker}</Kicker>
        <h2 className="mt-3 font-heading text-3xl md:text-4xl font-black text-[#1e3a8a]">
          {meta.title}
        </h2>
        <p className="mt-3 text-slate-600">{meta.desc}</p>
      </div>
      <div className="mt-8 grid gap-2 md:gap-3 grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
        {items.map((m, i) => (
          <WorkThumb key={m.id} m={m} onOpen={() => setLbIndex(i)} />
        ))}
      </div>
      {hasMore && (
        <div className="mt-8 flex justify-center">
          <Link
            to="/galeria"
            data-testid="btn-see-all-gallery"
            className="inline-flex items-center gap-2 bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-bold rounded-full px-6 py-3 shadow-lg hover:shadow-xl transition-all active:scale-95"
          >
            Ver todos os trabalhos
            <span className="inline-flex items-center justify-center bg-white/20 rounded-full w-6 h-6 text-xs font-black">
              {totalCount}
            </span>
          </Link>
        </div>
      )}
      <Lightbox
        items={items}
        index={lbIndex}
        onClose={() => setLbIndex(null)}
        onPrev={() => setLbIndex((i) => (i == null ? null : (i - 1 + items.length) % items.length))}
        onNext={() => setLbIndex((i) => (i == null ? null : (i + 1) % items.length))}
      />
    </Section>
  );
};

/* AppGallery — the two remaining admin/portal sections (unchanged UX) */
const AppGallery = () => {
  const [grouped, setGrouped] = useState({ portal_escola: [], painel_admin: [] });
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/landing/media?featured=true");
        const list = Array.isArray(r.data) ? r.data : [];
        const g = { portal_escola: [], painel_admin: [] };
        for (const m of list) {
          if (m.section === "portal_escola" || m.section === "painel_admin") {
            g[m.section].push(m);
          }
        }
        setGrouped(g);
      } catch {
        setGrouped({ portal_escola: [], painel_admin: [] });
      }
    })();
  }, []);

  // Pick items matching the current viewport: mobile viewport shows items
  // tagged ``mobile`` or ``both``, desktop viewport shows ``desktop`` or
  // ``both``. The ``trabalhos`` section is unaffected — it lives on
  // ``WorkGallery`` and is viewport-agnostic (real-world photos).
  const pickForDevice = (arr) =>
    arr.filter((m) => {
      const d = m.device || "both";
      if (d === "both") return true;
      return isMobile ? d === "mobile" : d === "desktop";
    });

  const filtered = {
    portal_escola: pickForDevice(grouped.portal_escola),
    painel_admin: pickForDevice(grouped.painel_admin),
  };
  const total = filtered.portal_escola.length + filtered.painel_admin.length;
  if (!total) return null;

  return (
    <>
      {["portal_escola", "painel_admin"].map((key) => {
        const items = filtered[key];
        if (!items.length) return null;
        const meta = SECTION_META[key];
        return (
          <Section key={key} id={meta.id} className={meta.bg}>
            <div className="text-center max-w-2xl mx-auto">
              <Kicker>{meta.kicker}</Kicker>
              <h2 className="mt-3 font-heading text-3xl md:text-4xl font-black text-[#1e3a8a]">
                {meta.title}
              </h2>
              <p className="mt-3 text-slate-600">{meta.desc}</p>
            </div>
            <div
              className={
                key === "painel_admin"
                  ? "mt-10 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                  : // Portal: horizontal snap-carousel on mobile, grid on >= sm.
                    "mt-10 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 scroll-smooth sm:mx-0 sm:px-0 sm:overflow-visible sm:grid sm:grid-cols-3 lg:grid-cols-5"
              }
            >
              {items.map((m) => (
                <div
                  key={m.id}
                  className={
                    key === "portal_escola"
                      ? "min-w-[72%] snap-center sm:min-w-0"
                      : ""
                  }
                >
                  <MediaFrame m={m} meta={meta} />
                </div>
              ))}
            </div>
            {key === "portal_escola" && items.length > 1 && (
              <p className="sm:hidden mt-2 text-center text-[11px] text-slate-500">
                deslize para o lado →
              </p>
            )}
          </Section>
        );
      })}
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Phone mockup — uses the first real portal screenshot available     */
/* ------------------------------------------------------------------ */
const PhoneMockup = () => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/landing/media");
        const img = (r.data || []).find((m) => m.kind === "image");
        if (img) setSrc(`/api/files/${img.storage_path}`);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  return (
    <div className="relative mx-auto w-full max-w-[280px] aspect-[9/19] rounded-[36px] bg-[#1e3a8a] p-2 shadow-2xl shadow-indigo-900/40">
      <div className="absolute left-1/2 -translate-x-1/2 top-2 w-24 h-5 bg-black rounded-b-2xl z-20" />
      <div className="relative w-full h-full rounded-[28px] bg-white overflow-hidden">
        {src ? (
          <img
            src={src}
            alt="Portal da escola — tela real do sistema AMC"
            loading="lazy"
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-b from-[#1e3a8a] to-[#4c1d95]" />
        )}
        <div className="pointer-events-none absolute inset-0 ring-1 ring-black/5 rounded-[28px]" />
      </div>
      <div className="absolute -right-6 top-20 hidden sm:block bg-white rounded-xl shadow-xl border border-slate-100 px-3 py-2 w-44">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#7c3aed] text-white flex items-center justify-center">
            <Bell className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] font-black text-[#1e3a8a] uppercase tracking-wider">Novo orçamento</p>
            <p className="text-[10px] text-slate-500 truncate">#142 recebido</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* How it works                                                        */
/* ------------------------------------------------------------------ */
const STEPS = [
  { n: "1", icon: Phone, title: "Solicite acesso", desc: "Chame no WhatsApp — enviamos seu convite de Diretor(a) em minutos." },
  { n: "2", icon: Download, title: "Entre no portal", desc: "Acesse pelo celular ou computador e instale o app na tela inicial." },
  { n: "3", icon: CheckCircle2, title: "Acompanhe tudo", desc: "Visualize orçamentos, aprove serviços e receba notificações da AMC." },
];

const HowItWorks = () => (
  <Section id="como-funciona" className="bg-white">
    <div className="text-center max-w-2xl mx-auto">
      <Kicker>Simples e rápido</Kicker>
      <h2 className="mt-3 font-heading text-3xl md:text-4xl font-black text-[#1e3a8a]">
        Comece em 3 passos
      </h2>
    </div>
    <div className="mt-10 grid md:grid-cols-3 gap-4">
      {STEPS.map((s) => (
        <div key={s.n} className="relative p-6 rounded-2xl bg-gradient-to-br from-[#1e3a8a] to-[#2b4bb0] text-white overflow-hidden">
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full" />
          <div className="relative flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <s.icon className="w-5 h-5" />
            </div>
            <span className="font-heading text-5xl font-black text-white/15">{s.n}</span>
          </div>
          <h3 className="relative mt-4 font-heading text-xl font-black">{s.title}</h3>
          <p className="relative mt-1 text-[13.5px] text-white/80 leading-relaxed">{s.desc}</p>
        </div>
      ))}
    </div>
    <div className="mt-10 flex justify-center">
      <PrimaryCTA testid="how-cta" large>Solicitar acesso Diretor(a)</PrimaryCTA>
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* Admin panel teaser                                                  */
/* ------------------------------------------------------------------ */
const AdminTeaser = () => (
  <Section className="bg-gradient-to-br from-[#0f172a] via-[#1e3a8a] to-[#4c1d95] text-white">
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <Kicker>Painel administrativo</Kicker>
        <h2 className="mt-3 font-heading text-3xl md:text-4xl font-black">
          Dashboard completo para a equipe AMC
        </h2>
        <p className="mt-3 text-white/80">
          Gerencie escolas, orçamentos, concorrências, financeiro, nota fiscal, FDP,
          observações e muito mais — em um único painel pensado para operar com velocidade.
        </p>
        <ul className="mt-6 space-y-2 text-sm">
          {[
            "Estatísticas monetárias em tempo real",
            "Alerta de papelada pendente",
            "Geração automática de concorrências por IA",
            "Fotos reais das escolas via Google Places",
            "Importação de dados legados",
          ].map((f) => (
            <li key={f} className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to="/login"
            data-testid="admin-teaser-login"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-[#1e3a8a] font-bold text-sm hover:bg-slate-100 shadow-lg"
          >
            <Shield className="w-4 h-4" /> Entrar como admin
          </Link>
          <a
            href={whatsappLink("Olá AMC Escolas! Gostaria de uma demonstração do sistema.")}
            target="_blank"
            rel="noreferrer"
            data-testid="admin-teaser-demo"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/30 text-white font-bold text-sm hover:bg-white/10"
          >
            <Phone className="w-4 h-4" /> Agendar demonstração
          </a>
        </div>
      </div>

      {/* Dashboard mockup */}
      <div className="relative">
        <div className="rounded-2xl bg-white text-slate-900 p-4 shadow-2xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Painel</p>
              <p className="font-heading font-black text-[#1e3a8a]">AMC Escolas · Dashboard</p>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-400" />
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[
              { l: "Escolas", v: "32", c: "from-indigo-50 to-indigo-100 text-indigo-700" },
              { l: "Orçamentos", v: "128", c: "from-violet-50 to-violet-100 text-violet-700" },
              { l: "FDP ativo", v: "R$ 48k", c: "from-amber-50 to-amber-100 text-amber-700" },
            ].map((s) => (
              <div key={s.l} className={`rounded-xl bg-gradient-to-br ${s.c} px-3 py-2`}>
                <p className="text-[9px] uppercase tracking-wider font-bold opacity-80">{s.l}</p>
                <p className="font-heading font-black text-lg mt-0.5">{s.v}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-xl bg-slate-50 p-3">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Receita mensal</p>
            <div className="mt-2 flex items-end gap-1.5 h-16">
              {[18, 32, 24, 38, 48, 54, 44, 60, 52, 72, 58, 66].map((h, i) => (
                <div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-[#7c3aed] to-[#a78bfa]" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between rounded-xl bg-rose-50 border border-rose-100 px-3 py-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-rose-500 text-white flex items-center justify-center">
                <Bell className="w-3.5 h-3.5" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-rose-800 leading-tight">Papelada pendente</p>
                <p className="text-[10px] text-rose-700/80">3 orçamentos aguardando NF</p>
              </div>
            </div>
            <span className="text-rose-600 text-[10px] font-bold">Ver →</span>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* Contact                                                             */
/* ------------------------------------------------------------------ */
const Contact = () => (
  <Section id="contato" className="bg-white">
    <div className="rounded-3xl bg-gradient-to-br from-[#7c3aed] to-[#5b21b6] text-white p-8 md:p-12 relative overflow-hidden">
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/10" />
      <div className="absolute -right-8 bottom-0 w-40 h-40 rounded-full bg-white/5" />
      <div className="relative grid md:grid-cols-2 gap-8 items-center">
        <div>
          <Kicker>Fale conosco</Kicker>
          <h2 className="mt-3 font-heading text-3xl md:text-4xl font-black leading-tight">
            Pronto para dar o próximo passo?
          </h2>
          <p className="mt-3 text-white/85 max-w-md">
            Entre em contato pelo WhatsApp ou e-mail. Em minutos sua escola está dentro
            do sistema AMC.
          </p>
          <div className="mt-6 space-y-3">
            <a
              href={whatsappLink("Olá AMC Escolas!")}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors border border-white/20"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-white/70">WhatsApp</p>
                <p className="font-heading font-black">(11) 95664-9968</p>
              </div>
            </a>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors border border-white/20"
            >
              <div className="w-10 h-10 rounded-xl bg-[#1e3a8a] flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-white/70">E-mail</p>
                <p className="font-heading font-black">{EMAIL}</p>
              </div>
            </a>
          </div>
        </div>
        <div className="relative text-center md:text-right">
          <PrimaryCTA testid="contact-cta" large className="!bg-white !text-[#7c3aed] from-white to-white shadow-xl">
            Solicitar acesso Diretor(a)
          </PrimaryCTA>
          <p className="text-xs text-white/70 mt-3">Resposta em menos de 1 hora útil</p>
        </div>
      </div>
    </div>
  </Section>
);

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */
const Footer = () => (
  <footer className="bg-[#0f172a] text-slate-300 pt-12 pb-8">
    <div className="max-w-7xl mx-auto px-4 md:px-6 grid md:grid-cols-3 gap-8">
      <div>
        <div className="bg-white/5 inline-block px-3 py-2 rounded-lg">
          <Logo compact withIcon />
        </div>
        <p className="text-xs mt-4 text-slate-400 max-w-xs leading-relaxed">
          AMC Escolas — manutenção e serviços profissionais para escolas municipais
          e estaduais, com portal digital para diretores(as).
        </p>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Portal</p>
        <ul className="mt-3 space-y-2 text-sm">
          <li><Link to="/escola/login" className="hover:text-white">Entrar como diretor(a)</Link></li>
          <li><Link to="/login" className="hover:text-white">Entrar como admin</Link></li>
          <li><a href="#plataforma" className="hover:text-white">Recursos da plataforma</a></li>
          <li><a href="#como-funciona" className="hover:text-white">Como funciona</a></li>
        </ul>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Contato</p>
        <ul className="mt-3 space-y-2 text-sm">
          <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-emerald-400" /> (11) 95664-9968</li>
          <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-violet-400" /> {EMAIL}</li>
          <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-400" /> {SITE_DOMAIN}</li>
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 md:px-6 mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-slate-500">
      <p>© {new Date().getFullYear()} AMC Escolas — Todos os direitos reservados</p>
      <p>Cuidamos da sua escola, você cuida do futuro.</p>
    </div>
  </footer>
);

/* ------------------------------------------------------------------ */
/* FDP preview — teaser block that routes to the public page           */
/* ------------------------------------------------------------------ */
const FdpPreview = () => (
  <section id="fdp-preview" className="relative px-4 md:px-6 py-16 md:py-24 overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-emerald-50/60">
    <div className="absolute -top-20 -left-24 w-96 h-96 rounded-full bg-emerald-300/30 blur-3xl" />
    <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-teal-300/30 blur-3xl" />

    <div className="relative max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
      <div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-600/10 border border-emerald-500/30 text-emerald-700 text-[10px] font-black uppercase tracking-widest">
          <Sparkles className="w-3 h-3" /> Modalidade exclusiva
        </span>
        <h2 className="mt-4 font-heading text-3xl md:text-5xl font-black text-[#0b1536] leading-[1.05]">
          Faça agora,{" "}
          <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
            pague depois.
          </span>
        </h2>
        <p className="mt-4 text-slate-600 text-[14px] md:text-base leading-relaxed max-w-xl">
          O <strong className="text-emerald-700">Faz Depois Paga</strong> é o
          jeito prático da sua escola resolver manutenções sem esperar aprovação
          — você acumula serviços num único ciclo e paga uma só nota ao final.
        </p>

        {/* 3 quick benefits */}
        <ul className="mt-6 grid sm:grid-cols-3 gap-3">
          {[
            { icon: Zap, title: "Serviço imediato", desc: "Começa no mesmo dia." },
            { icon: Shield, title: "Zero risco", desc: "Paga só depois do serviço feito." },
            { icon: Receipt, title: "Uma nota ao final", desc: "Menos papelada pra você." },
          ].map((b) => (
            <li
              key={b.title}
              className="rounded-xl border border-emerald-200 bg-white p-3 flex gap-2.5 shadow-sm"
            >
              <div className="w-9 h-9 shrink-0 rounded-lg bg-emerald-50 flex items-center justify-center">
                <b.icon className="w-4 h-4 text-emerald-600" strokeWidth={2.4} />
              </div>
              <div className="min-w-0">
                <p className="text-[12.5px] font-black text-[#0b1536] leading-tight">{b.title}</p>
                <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{b.desc}</p>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <Link
            to="/faz-depois-paga"
            data-testid="fdp-saiba-mais"
            className="group inline-flex items-center justify-center gap-2 h-13 px-6 py-3.5 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-600 text-white font-black text-sm shadow-xl shadow-emerald-500/40 transition-all active:scale-[0.98]"
          >
            <Handshake className="w-5 h-5" strokeWidth={2.4} />
            Saiba mais sobre o Faz Depois Paga
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.6} />
          </Link>
          <a
            href={whatsappLink("Olá AMC Escolas! Quero contratar meu primeiro orçamento e garantir minha escola no sistema Faz Depois Paga.")}
            target="_blank"
            rel="noreferrer"
            data-testid="fdp-preview-whatsapp"
            className="inline-flex items-center justify-center gap-2 h-13 px-6 py-3.5 rounded-2xl bg-white hover:bg-emerald-50 text-emerald-700 border-2 border-emerald-500 font-bold text-[13px] transition-colors"
          >
            Quero garantir minha escola
          </a>
        </div>
      </div>

      {/* Visual: stacked card showing a FDP badge on a budget */}
      <div className="relative h-[320px] md:h-[380px] flex items-center justify-center">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-72 h-72 rounded-[3rem] bg-gradient-to-br from-emerald-400/30 to-teal-500/30 blur-2xl" />
        </div>
        <div className="relative w-full max-w-md rounded-3xl bg-white border border-slate-200 shadow-2xl shadow-emerald-900/20 p-5 rotate-[-2deg]">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-black text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full uppercase tracking-widest">
              <Handshake className="w-3 h-3" /> FDP ativo
            </span>
            <span className="text-[10px] text-slate-400 font-semibold">Ciclo em andamento</span>
          </div>
          <h3 className="mt-3 font-heading text-lg font-black text-[#0b1536]">
            APM da EMEB Carolina
          </h3>
          <div className="mt-3 grid grid-cols-2 gap-2.5">
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-black">Prazo</p>
              <p className="mt-0.5 font-heading text-base font-black text-emerald-600">6 meses</p>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-black">Valor</p>
              <p className="mt-0.5 font-heading text-base font-black text-emerald-600">R$ 15.000</p>
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full w-3/5 bg-gradient-to-r from-emerald-500 to-teal-400" />
          </div>
          <div className="mt-2 flex justify-between text-[10px] font-semibold text-slate-500">
            <span>R$ 8.980 utilizados</span>
            <span className="text-emerald-700">60%</span>
          </div>
        </div>
        <div className="absolute -bottom-2 right-2 md:right-6 rotate-[6deg] bg-emerald-500 text-white px-4 py-2 rounded-xl text-[11px] font-black shadow-xl">
          Serviço começa no mesmo dia
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/* Floating WhatsApp bubble                                            */
/* ------------------------------------------------------------------ */
const FloatingWhatsApp = () => (
  <a
    href={whatsappLink(ACCESS_MESSAGE)}
    target="_blank"
    rel="noreferrer"
    data-testid="floating-whatsapp"
    className="fixed bottom-5 right-5 z-40 inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500 text-white shadow-2xl shadow-emerald-900/30 hover:scale-110 transition-transform"
    aria-label="Falar no WhatsApp"
  >
    <Phone className="w-6 h-6" />
    <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-30" />
  </a>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Header />
      <Hero />
      <FdpPreview />
      <Stats />
      <WorkGallery />
      <Services />
      <Platform />
      <AppGallery />
      <Testimonials />
      <HowItWorks />
      <AdminTeaser />
      <Contact />
      <Footer />
      <FloatingWhatsApp />
      <MascotChat />
    </div>
  );
}
